import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import ageService, { AgeRange } from '../../services/ageService';

interface AgeState extends LoadingState {
  ages: AgeRange[];
  selectedAge: AgeRange | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: AgeState = {
  ages: [],
  selectedAge: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchAges = createAsyncThunk(
  'ages/fetchAges',
  async (params: { page?: number; limit?: number; search?: string; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 5, search = '', append = false } = params;
      const response = await ageService.getActiveAgeRanges({ page, limit });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch ages');
    }
  }
);

export const createAge = createAsyncThunk(
  'ages/createAge',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await ageService.createAgeRange(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create age');
    }
  }
);

const ageSlice = createSlice({
  name: 'ages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedAge: (state, action: PayloadAction<AgeRange | null>) => {
      state.selectedAge = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.ages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Ages
      .addCase(fetchAges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAges.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.ages.push(...data.data as any);
        } else {
          state.ages = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchAges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Age
      .addCase(createAge.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAge.fulfilled, (state, action) => {
        state.isLoading = false;
        const newAge = action.payload;
        
        // Create a properly typed object for WritableDraft
        const ageToAdd: AgeRange = {
          _id: newAge._id,
          name: newAge.name,
          slug: newAge.slug,
          status: newAge.status,
          isDeleted: newAge.isDeleted,
          createdBy: newAge.createdBy,
          createdAt: newAge.createdAt,
          updatedAt: newAge.updatedAt
        };
        
        state.ages.unshift(ageToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createAge.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedAge,
  setSearchQuery,
  resetPagination,
} = ageSlice.actions;

export default ageSlice.reducer;
